import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type PartnerOverviewResult } from '@whinerp/client';
import { formatDate } from '@whinerp/contracts/format';
import { Button, CapabilityUnavailable, ErrorState, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useFormat, useT, useErrorMessage } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Partners and what they registered (F-061).
 *
 * **The collision is shown here and nowhere else.** The partner who registered
 * second was told "under review", exactly as if nothing collided; this is the
 * one place somebody deciding learns that it did. It is not on the deal's own
 * page, because a reader of that deal may not be able to read the other one.
 */

type Overview = PartnerOverviewResult;
type Registration = Overview['registrations'][number];

const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

/**
 * Accept, or reject with a reason the partner will read.
 *
 * Shared with the deal page, so the two places a registration is decided
 * cannot disagree about what rejecting needs.
 */
export function RegistrationDecision({
  dealId,
  onDecided,
}: {
  dealId: string;
  onDecided: () => Promise<unknown>;
}): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const decide = useMutation<unknown, ApiError, { decision: 'accept' | 'reject'; note?: string }>({
    mutationFn: (body) => api.decideRegistration({ path: { dealId }, body }),
    onSuccess: async () => {
      setRejecting(false);
      setNote('');
      await onDecided();
    },
  });

  return (
    <div data-registration-decision={dealId} className="flex flex-col gap-2">
      {decide.error ? (
        <p data-registration-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {errorText(decide.error)}
        </p>
      ) : null}
      {rejecting ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense text-ink-600">
            {t('partner.rejectReason')}
            <input
              data-registration-note
              className={INPUT}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          <Button
            intent="danger"
            size="small"
            data-registration-confirm-reject
            disabled={decide.isPending}
            onClick={() => decide.mutate({ decision: 'reject', note })}
          >
            {t('partner.reject')}
          </Button>
          <Button intent="ghost" size="small" data-registration-cancel onClick={() => setRejecting(false)}>
            {t('partner.cancel')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            intent="primary"
            size="small"
            data-registration-accept
            disabled={decide.isPending}
            onClick={() => decide.mutate({ decision: 'accept' })}
          >
            {t('partner.accept')}
          </Button>
          <Button intent="ghost" size="small" data-registration-reject onClick={() => setRejecting(true)}>
            {t('partner.reject')}
          </Button>
        </div>
      )}
    </div>
  );
}

export function PartnersPanel(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const entitlement = useEntitlement('F-061');
  const permitted = can('partner.manage');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  const overview = useQuery<Overview, ApiError>({
    queryKey: ['partners'],
    queryFn: () => api.partnerOverview({}),
    enabled: entitlement.enabled && permitted,
  });
  const refresh = (): Promise<unknown> => queryClient.invalidateQueries({ queryKey: ['partners'] });

  const share = useMutation<unknown, ApiError>({
    mutationFn: () => api.savePartnerCollateral({ body: { title, url } }),
    onSuccess: async () => {
      setTitle('');
      setUrl('');
      await refresh();
    },
  });
  const unshare = useMutation<unknown, ApiError, string>({
    mutationFn: (collateralId) => api.removePartnerCollateral({ path: { collateralId } }),
    onSuccess: refresh,
  });

  const heading = (
    <div className="flex flex-col gap-1">
      <SectionLabel>{t('partner.title')}</SectionLabel>
      <p className="text-meta text-ink-meta">{t('partner.body')}</p>
    </div>
  );

  if (entitlement.state === 'off' || entitlement.state === 'plan-locked') {
    return (
      <Panel data-region="partners" id="partners">
        <div className="flex flex-col gap-3">
          {heading}
          <CapabilityUnavailable
            kind={entitlement.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('partner.title')}
            title={t('capability.moduleOff.title')}
            body={t('capability.moduleOff.body', { values: { capability: t('partner.title') } })}
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }

  if (!permitted) {
    // Said, not blank: an empty panel would read as "no partners".
    return (
      <Panel data-region="partners" id="partners">
        <div className="flex flex-col gap-3">
          {heading}
          <p data-partners-not-permitted className="text-meta text-ink-meta">
            {t('partner.notPermitted')}
          </p>
        </div>
      </Panel>
    );
  }

  if (overview.isPending) return <Skeleton className="h-block-md w-full" />;
  if (overview.isError) {
    return (
      <ErrorState title={t('state.error')} body={errorText(overview.error)} onRetry={() => void overview.refetch()} />
    );
  }

  const { partners, registrations, collateral } = overview.data;

  return (
    <Panel data-region="partners" id="partners">
      <div className="flex flex-col gap-4">
        {heading}

        <section className="flex flex-col gap-2">
          <h3 className="text-dense font-medium text-ink">{t('partner.registrations')}</h3>
          {registrations.length === 0 ? (
            <p data-registrations-none className="text-meta text-ink-meta">
              {t('partner.registrations.none')}
            </p>
          ) : (
            <ul className="flex flex-col">
              {registrations.map((registration) => (
                <RegistrationRow key={registration.dealId} registration={registration} onDecided={refresh} />
              ))}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-dense font-medium text-ink">{t('partner.partners')}</h3>
          {partners.length === 0 ? (
            <p data-partners-none className="text-meta text-ink-meta">
              {t('partner.partners.none')}
            </p>
          ) : (
            <ul className="flex flex-col">
              {partners.map((partner) => (
                <li
                  key={partner.accountId}
                  data-partner={partner.accountId}
                  className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0"
                >
                  <Link to={`/accounts/${partner.accountId}`} className="min-w-0 flex-1 truncate text-body text-ink hover:underline">
                    {partner.name}
                  </Link>
                  <span className="text-meta text-ink-meta">
                    {t('partner.protectionDays', { values: { days: String(partner.protectionDays) } })} ·{' '}
                    {t('partner.people', { values: { count: String(partner.contacts.length) } })}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-meta text-ink-meta">{t('partner.partners.help')}</p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-dense font-medium text-ink">{t('partner.collateral')}</h3>
          {collateral.length === 0 ? (
            <p data-collateral-none className="text-meta text-ink-meta">
              {t('partner.collateral.none')}
            </p>
          ) : (
            <ul className="flex flex-col">
              {collateral.map((link) => (
                <li
                  key={link.collateralId}
                  data-collateral={link.collateralId}
                  className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0"
                >
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate text-body text-ink hover:underline">
                    {link.title}
                  </a>
                  <Button
                    intent="ghost"
                    size="small"
                    data-collateral-remove={link.collateralId}
                    onClick={() => unshare.mutate(link.collateralId)}
                  >
                    {t('partner.collateral.remove')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {share.error ? (
            <p data-collateral-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
              {errorText(share.error)}
            </p>
          ) : null}
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense text-ink-600">
              {t('partner.collateral.title')}
              <input data-collateral-title className={INPUT} value={title} onChange={(event) => setTitle(event.target.value)} />
            </label>
            <label className="flex min-w-0 flex-1 flex-col gap-1 text-dense text-ink-600">
              {t('partner.collateral.url')}
              <input
                data-collateral-url
                type="url"
                className={INPUT}
                value={url}
                onChange={(event) => setUrl(event.target.value)}
              />
            </label>
            <Button intent="secondary" size="small" data-collateral-share disabled={share.isPending} onClick={() => share.mutate()}>
              {t('partner.collateral.share')}
            </Button>
          </div>
        </section>
      </div>
    </Panel>
  );

}

function RegistrationRow({
  registration,
  onDecided,
}: {
  registration: Registration;
  onDecided: () => Promise<unknown>;
}): JSX.Element {
  const t = useT();
  const format = useFormat();
  return (
    <li
      data-registration={registration.dealId}
      data-registration-status={registration.status}
      className="flex flex-col gap-2 border-t border-t-rule py-2 first:border-t-0"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Link to={`/deals/${registration.dealId}`} className="min-w-0 flex-1 truncate text-body text-ink hover:underline">
          {registration.dealName} · {registration.customer}
        </Link>
        <span className="rounded-chip bg-sunken px-2 py-0.5 text-meta text-ink-600">
          {t(`partner.status.${registration.status}` as 'partner.status.pending')}
        </span>
      </div>
      <p className="text-meta text-ink-meta">
        {t('partner.registeredBy', {
          values: {
            person: registration.registeredBy ?? t('partner.someone'),
            partner: registration.partner,
            date: formatDate(registration.registeredAt, format),
          },
        })}
        {registration.protectedUntil
          ? ` · ${t('partner.protectedUntil', { values: { date: formatDate(registration.protectedUntil, format) } })}`
          : ''}
      </p>
      {registration.conflict ? (
        <p data-registration-conflict className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">
          {registration.conflict.partner
            ? t('partner.conflict.partner', {
                values: { deal: registration.conflict.dealName, partner: registration.conflict.partner },
              })
            : t('partner.conflict.direct', { values: { deal: registration.conflict.dealName } })}
        </p>
      ) : null}
      {registration.status === 'pending' ? (
        <RegistrationDecision dealId={registration.dealId} onDecided={onDecided} />
      ) : null}
    </li>
  );
}
